import { Checkbox, Col, Form, Input, message, Modal, Row, Switch } from "antd";
import { Rule } from "antd/lib/form";
import TextArea from "antd/lib/input/TextArea";
import { roleApi } from "api/role.api";
import clsx from "clsx";
import {
  allPermissionItems,
  PERMISSION_ACTIONS,
  permissionKey,
  permissionMenu,
  PermissionAction,
} from "config/permissionMenu";
import React, { useImperativeHandle, useMemo, useState } from "react";
import { permissionStore } from "store/permissionStore";
import { userStore } from "store/userStore";
import { ModalStatus } from "types/modal";
import { Permission, Role } from "types/role";

const rules: Rule[] = [{ required: true, message: "Bắt buộc" }];

export interface RoleModal {
  handleCreate: () => void;
  handleUpdate: (Role: Role) => void;
}
interface RoleModalProps {
  onClose: () => void;
  onSubmitOk: () => void;
}

const ALL_KEYS = allPermissionItems().map((e) => permissionKey(e.path, e.action));

export const RoleModal = React.forwardRef(({ onClose, onSubmitOk }: RoleModalProps, ref) => {
  const [form] = Form.useForm<Role>();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [status, setStatus] = useState<ModalStatus>("create");
  const [role, setRole] = useState<Role>();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [originPermissions, setOriginPermissions] = useState<Permission[]>([]);
  const [activeGroupKey, setActiveGroupKey] = useState(permissionMenu[0]?.key);

  const activeGroup = permissionMenu.find((e) => e.key === activeGroupKey) || permissionMenu[0];
  const isAllChecked = ALL_KEYS.length > 0 && ALL_KEYS.every((key) => checked.has(key));

  // Chỉ hiển thị các cột hành động mà nhóm đang chọn có sử dụng
  const columns = useMemo(() => {
    const used = new Set<PermissionAction>(activeGroup?.features.flatMap((e) => e.actions));
    return PERMISSION_ACTIONS.filter((e) => used.has(e.key));
  }, [activeGroup]);

  const fetchOriginPermissions = async () => {
    const res = await roleApi.getPermission();
    setOriginPermissions(res.data);
  };

  useImperativeHandle<any, RoleModal>(
    ref,
    () => ({
      handleCreate() {
        form.resetFields();
        setChecked(new Set());
        setActiveGroupKey(permissionMenu[0]?.key);
        setVisible(true);
        setStatus("create");
        setRole(undefined);
        fetchOriginPermissions();
      },
      async handleUpdate(Role: Role) {
        const detailRole = await roleApi.findOne(Role.id);

        form.setFieldsValue({ ...detailRole.data });
        setChecked(
          new Set(
            detailRole.data.permissions.map((e: Permission) => permissionKey(e.path, e.action || "VIEW")),
          ),
        );
        setActiveGroupKey(permissionMenu[0]?.key);
        setVisible(true);
        setStatus("update");
        setRole(Role);
        fetchOriginPermissions();
      },
    }),
    [],
  );

  const toggleAll = (value: boolean) => {
    setChecked(new Set(value ? ALL_KEYS : []));
  };

  const toggleAction = (path: string, action: PermissionAction, value: boolean) => {
    const next = new Set(checked);
    const key = permissionKey(path, action);

    if (value) {
      next.add(key);
    } else {
      next.delete(key);
      // Bỏ quyền Xem thì bỏ luôn các quyền còn lại của chức năng
      if (action === "VIEW") {
        for (const item of PERMISSION_ACTIONS) {
          next.delete(permissionKey(path, item.key));
        }
      }
    }
    setChecked(next);
  };

  const handleClose = () => {
    form.resetFields();
    setChecked(new Set());
    setVisible(false);
    onClose();
  };

  const handleSubmit = async () => {
    await form.validateFields();

    const permissionIds = Array.from(checked)
      .map((key) => {
        const [path, action] = key.split("|");
        return originPermissions.find((p) => p.path === path && (p.action || "VIEW") === action)?.id;
      })
      .filter(Boolean);

    if (checked.size && permissionIds.length < checked.size) {
      message.warning('Một số quyền chưa có trong hệ thống, hãy bấm "Import routes" rồi thử lại');
    }

    const data = { role: form.getFieldsValue(), permissionIds };

    setLoading(true);
    try {
      if (status === "create") {
        await roleApi.create(data);
        message.success("Tạo mới thành công");
      } else {
        await roleApi.update(role?.id || 0, data);
        message.success("Cập nhật thành công");
        if (role?.id && role.id === userStore.info.role?.id && !userStore.info.role?.isAdmin) {
          await permissionStore.fetchPermissions(role.id);
        }
      }
      handleClose();
      onSubmitOk();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      onCancel={handleClose}
      open={visible}
      title={status === "create" ? "Tạo mới vai trò" : "Cập nhật vai trò"}
      style={{ top: 20 }}
      width={1100}
      okText="Đồng ý"
      cancelText="Hủy"
      confirmLoading={loading}
      onOk={handleSubmit}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item label="Tên vai trò" name="name" rules={rules}>
              <Input placeholder="" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item label="Mô tả vai trò" name="description" rules={rules}>
              <TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div className="flex gap-6">
        {/* Danh sách nhóm menu */}
        <div className="flex flex-col gap-3" style={{ width: 240, flexShrink: 0 }}>
          {permissionMenu.map((group) => {
            const isActive = group.key === activeGroup?.key;
            return (
              <button
                key={group.key}
                type="button"
                onClick={() => setActiveGroupKey(group.key)}
                className={clsx(
                  "flex items-center justify-between rounded-lg border px-4 py-3 text-left font-semibold",
                  isActive
                    ? "border-[#f58a07] bg-[#f58a07] text-white"
                    : "border-gray-200 bg-white text-gray-800 hover:border-[#f58a07]",
                )}
              >
                <span>{group.title}</span>
                <span>{isActive ? "+" : "−"}</span>
              </button>
            );
          })}
        </div>

        {/* Bảng chức năng × hành động */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <Switch checked={isAllChecked} onChange={toggleAll} />
            <span>Có toàn quyền trên tất cả danh mục</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border border-gray-200 text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-3 py-2 text-left font-semibold">Chức năng</th>
                  {columns.map((column) => (
                    <th key={column.key} className="border border-gray-200 px-3 py-2 text-center font-semibold">
                      {column.title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {activeGroup?.features.map((feature) => {
                  const canView = checked.has(permissionKey(feature.path, "VIEW"));
                  return (
                    <tr key={feature.path}>
                      <td className="border border-gray-200 px-3 py-2">{feature.title}</td>
                      {columns.map((column) => (
                        <td key={column.key} className="border border-gray-200 px-3 py-2 text-center">
                          {feature.actions.includes(column.key) && (
                            <Checkbox
                              checked={checked.has(permissionKey(feature.path, column.key))}
                              disabled={column.key !== "VIEW" && !canView}
                              onChange={(e) => toggleAction(feature.path, column.key, e.target.checked)}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
});
